import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import uniqueId from 'lodash/uniqueId';
import { usePostHog } from 'posthog-js/react';
import { useSearchParams } from 'react-router';

import saveUserHistory, {
  deriveConversationName
} from '@/features/AIChat/AskGXDrawer/utils/saveUserHistory';
import type { ChatContextValue } from '@/features/AIChat/context/ChatContext';
import { ChatContext } from '@/features/AIChat/context/ChatContext';
import type {
  AIMessage,
  ContentPart,
  HumanMessage,
  Message
} from '@/features/AIChat/context/utils';
import {
  appendPartToMessages,
  useInitializeChat,
  useSendMessage
} from '@/features/AIChat/context/utils';
import isAiMessage from '@/features/AIChat/utils/isAiMessage';
import isHumanMessage from '@/features/AIChat/utils/isHumanMessage';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { useTripTokenFromStorage } from '@/services/storage';

export const ChatProvider = ({ open, children }: { open: boolean; children: React.ReactNode }) => {
  const { userEmail } = useUserDetails();
  const posthog = usePostHog();
  const dispatch = useAppDispatch();
  const userConfig = useAppSelector(state => state.userConfig.userConfig);
  const isUserConfigLoaded = useAppSelector(state => state.userConfig.status === 'fulfilled');
  const tripToken = useTripTokenFromStorage();

  const [conversationId, setConversationId] = useState<string | null>('');
  const [messages, setMessages] = useState<Message[]>([]);

  const [animatedHumanId, setAnimatedHumanId] = useState<string | null>(null);
  const [animatedText, setAnimatedText] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const version = useRef(0);
  const messagesRef = useRef<Message[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const initChat = useInitializeChat();

  const appendPart = useCallback((part: ContentPart) => {
    setMessages(prev => {
      const next = appendPartToMessages(prev, part);
      messagesRef.current = next;
      return next;
    });
  }, []);

  const saveMessageToHistory = useCallback(() => {
    if (!conversationId || !isUserConfigLoaded) return;
    const timestamp = new Date().toISOString();
    const existing = userConfig.chatHistory;
    const idx = existing.findIndex(c => c.conversationId === conversationId);
    const updatedConversation = {
      conversationId,
      messages: messagesRef.current,
      timestamp,
      name: deriveConversationName(messagesRef.current)
    };
    saveUserHistory({
      userConfig,
      dispatch,
      updatedConversation,
      existing,
      idx
    });
  }, [conversationId, dispatch, userConfig, isUserConfigLoaded]);

  const sendPosthogEvent = useCallback(() => {
    if (!conversationId) return;

    const humanMsg = messagesRef.current.at(-2); // The second-to-last message is human
    const aiMsg = messagesRef.current.at(-1); // The last message is AI

    if (!isHumanMessage(humanMsg) || !isAiMessage(aiMsg)) return; // type check

    const prompt = humanMsg.text;
    const response = aiMsg.content;

    const event = {
      timestamp: new Date().toISOString(),
      source: 'gxgo',
      conversation_id: conversationId,
      email: userEmail,
      prompt,
      response
    };

    posthog.capture('ai_conversation', event);
  }, [conversationId, posthog, userEmail]);

  const { mutation: sendMutation, cancel: cancelSend } = useSendMessage({
    onPart: appendPart,
    onComplete: () => {
      saveMessageToHistory();
      sendPosthogEvent();
    }
  });

  useEffect(() => {
    if (open && conversationId === '' && !initChat.isPending && tripToken) {
      setMessages([]);
      cancelSend();
      sendMutation.reset();
      initChat.mutate(undefined, {
        onSuccess: data => {
          setConversationId(data.conversation_id);
          version.current = data.version ? Number(data.version) : 0;
        },
        onError: error => {
          console.error('Error initializing chat:', error);
          setConversationId(null);
        }
      });
    }
  }, [open, conversationId, initChat, sendMutation, cancelSend, tripToken]);

  const sendMessage = useCallback(
    (text: string, options?: { addHuman?: boolean }) => {
      if (!conversationId || !text.trim()) return;

      setMessages(prev => {
        const now = new Date().toISOString();
        const human: HumanMessage | null =
          options?.addHuman === false
            ? null
            : {
                id: uniqueId(now),
                sender: 'human',
                text,
                timestamp: now
              };
        const ai: AIMessage = {
          id: uniqueId(now),
          sender: 'ai',
          timestamp: now,
          content: []
        };
        const next: Message[] = human ? [...prev, human, ai] : [...prev, ai];
        messagesRef.current = next;
        return next;
      });

      sendMutation.mutate({
        conversationId,
        message: text,
        userId: userEmail || ''
      });
    },
    [conversationId, userEmail, sendMutation]
  );

  const handleSend = useCallback(() => {
    if (!inputMessage.trim() || animatedText) return;
    sendMessage(inputMessage);
    setInputMessage('');
  }, [inputMessage, sendMessage, animatedText]);

  useEffect(() => {
    // Cancel in-flight stream when conversation switches
    if (conversationId) {
      cancelSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const hasProcessedPromptRef = useRef(false);
  useEffect(() => {
    if (hasProcessedPromptRef.current) return;
    const prompt = searchParams.get('prompt');
    if (!prompt) return;

    if (!conversationId) return;

    hasProcessedPromptRef.current = true;

    setMessages(prev => {
      const now = new Date().toISOString();
      const human: HumanMessage = {
        id: uniqueId(now),
        sender: 'human',
        text: prompt,
        timestamp: now
      };
      messagesRef.current = [...prev, human];
      setAnimatedHumanId(human.id);
      setAnimatedText(prompt);
      return messagesRef.current;
    });

    setSearchParams(
      prev => {
        prev.delete('prompt');
        return prev;
      },
      { replace: true }
    );
  }, [conversationId, searchParams, setSearchParams, sendMessage]);

  const onAnimatedComplete = useCallback(() => {
    if (!animatedText) return;
    // After animation, send the prompt but avoid adding another human message
    sendMessage(animatedText, { addHuman: false });
    // Clear animation state
    setAnimatedHumanId(null);
    setAnimatedText(null);
  }, [animatedText, sendMessage]);

  const value = useMemo<ChatContextValue>(
    () => ({
      messages,
      setMessages,
      inputMessage,
      setInputMessage,
      handleSend,
      sendMessage,
      isInitializing: initChat.isPending,
      isSending: sendMutation.isPending,
      setConversationId,
      conversationId,
      version: version.current,
      animatedHumanId,
      animatedText,
      onAnimatedComplete
    }),
    [
      messages,
      inputMessage,
      handleSend,
      sendMessage,
      initChat.isPending,
      sendMutation.isPending,
      conversationId,
      animatedHumanId,
      animatedText,
      onAnimatedComplete
    ]
  );

  return <ChatContext value={value}>{children}</ChatContext>;
};
